import React, { useEffect, useRef } from 'react';
import Hls from 'hls.js';

const CF_CUSTOMER = 'customer-vbbn7p39z8nnfjwk.cloudflarestream.com';

/**
 * HdVideo - Forces highest quality playback via HLS.js
 * Replaces Cloudflare Stream iframes to guarantee max resolution.
 *
 * Props:
 *  - uid: Cloudflare Stream video UID
 *  - className: CSS classes for the <video> element
 *  - autoplay: boolean (default false)
 *  - muted: boolean (default false)
 *  - controls: boolean (default true)
 *  - loop: boolean (default false)
 *  - aspectClass: Tailwind aspect ratio class (default "aspect-[9/16]")
 *  - testId: data-testid value
 */
const HdVideo = ({ uid, className = '', autoplay = false, muted = false, controls = true, loop = false, aspectClass = 'aspect-[9/16]', fill = false, testId }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !uid) return;

    const manifestUrl = `https://${CF_CUSTOMER}/${uid}/manifest/video.m3u8`;

    if (Hls.isSupported()) {
      const hls = new Hls({
        startLevel: -1,
        capLevelToPlayerSize: false,
        maxBufferLength: 60,
        maxMaxBufferLength: 120,
      });
      hlsRef.current = hls;
      hls.loadSource(manifestUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
        // Force highest quality level available
        const maxLevel = data.levels.length - 1;
        hls.currentLevel = maxLevel;
        hls.loadLevel = maxLevel;
        // Disable auto quality switching
        hls.nextLevel = maxLevel;
        if (autoplay) video.play().catch(() => {});
      });
      // Keep forcing highest level if ABR tries to switch down
      hls.on(Hls.Events.LEVEL_SWITCHING, (_, data) => {
        const maxLevel = hls.levels.length - 1;
        if (data.level < maxLevel) {
          hls.currentLevel = maxLevel;
          hls.loadLevel = maxLevel;
        }
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      video.src = manifestUrl;
      if (autoplay) video.play().catch(() => {});
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [uid, autoplay]);

  // Fill mode: video takes full size of its parent (for modals)
  if (fill) {
    return (
      <video
        ref={videoRef}
        className={`w-full h-full object-contain bg-black ${className}`}
        controls={controls}
        playsInline
        muted={muted}
        loop={loop}
        preload="auto"
        data-testid={testId}
      />
    );
  }

  // Normal mode: aspect-ratio wrapper constrains the video
  return (
    <div className={`relative w-full ${aspectClass} overflow-hidden`}>
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover bg-black"
        controls={controls}
        playsInline
        muted={muted}
        loop={loop}
        preload="auto"
        data-testid={testId}
      />
    </div>
  );
};

export default HdVideo;
